import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import { IGenerateQuestionsProps, IQasQuestionMakerState, IResponseGenerateQuestionStatus } from "@/types/qas/qasQuestionMakerTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasQuestionMakerState = {
	questionMakerStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	questionMakerStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	questionMakerStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
	},
};

// генерация вопросов
export const generateQuestions = createAsyncThunk(
	"qasQuestionMaker/start",
	async ({ categoryId, documentId, channelId, modelGpt, generateAnswers, promptTemplateId }: IGenerateQuestionsProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_MAKER_START}`, {
				params: {
					category: categoryId,
					doc: documentId,
					channel: channelId,
					model: modelGpt,
					generateAnswers,
					template: promptTemplateId,
				},
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// остановка генерации вопросов
export const stopGenerateQuestions = createAsyncThunk("qasQuestionMaker/stop", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_MAKER_STOP}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус генерации вопросов
export const getStatusGenerateQuestions = createAsyncThunk(
	"qasQuestionMaker/status",
	async (_, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseGenerateQuestionStatus> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.QUESTION_MAKER_STATUS}`);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasQuestionMaker.questionMakerStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

const qasQuestionMakerSlice = createSlice({
	name: "qasQuestionMaker",
	initialState,
	reducers: {
		// очистка state
		clearState: (state) => {
			state.questionMakerStart = initialState.questionMakerStart;
			state.questionMakerStop = initialState.questionMakerStop;
			state.questionMakerStatus = initialState.questionMakerStatus;
		},
		// очистка статуса старта генерации вопросов
		clearQuestionMakerStart: (state) => {
			state.questionMakerStart = initialState.questionMakerStart;
		},
		// очистка статуса остановки генерации вопросов
		clearQuestionMakerStop: (state) => {
			state.questionMakerStop = initialState.questionMakerStop;
		},
		// очистка статуса процесса генерации вопросов
		clearQuestionMakerStatus: (state) => {
			state.questionMakerStatus = initialState.questionMakerStatus;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(generateQuestions.pending, (state) => {
				state.questionMakerStart.status = RequestStatus.LOADING;
			})
			.addCase(generateQuestions.fulfilled, (state, action) => {
				if (action.payload) {
					state.questionMakerStart.status = RequestStatus.IDLE;
					state.questionMakerStart.error = action.payload.error;
					state.questionMakerStart.message = action.payload.message;
				} else state.questionMakerStart.status = RequestStatus.FAILED;
			})
			.addCase(generateQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionMakerStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionMakerStart.error = error;
				state.questionMakerStart.message = message;
			})
			.addCase(stopGenerateQuestions.pending, (state) => {
				state.questionMakerStop.status = RequestStatus.LOADING;
			})
			.addCase(stopGenerateQuestions.fulfilled, (state, action) => {
				if (action.payload) {
					state.questionMakerStop.status = RequestStatus.IDLE;
					state.questionMakerStop.error = action.payload.error;
					state.questionMakerStop.message = action.payload.message;
				} else state.questionMakerStop.status = RequestStatus.FAILED;
			})
			.addCase(stopGenerateQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionMakerStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.questionMakerStop.error = error;
				state.questionMakerStop.message = message;
			})
			.addCase(getStatusGenerateQuestions.pending, (state) => {
				state.questionMakerStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusGenerateQuestions.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.questionMakerStatus.requestStatus = RequestStatus.IDLE;
					state.questionMakerStatus.error = action.payload.error;
					state.questionMakerStatus.responseStatus = action.payload.status;
					state.questionMakerStatus.progress = action.payload.progress;
				} else state.questionMakerStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusGenerateQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.questionMakerStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.questionMakerStatus.error = error.toString();
			});
	},
});

export const { clearState, clearQuestionMakerStart, clearQuestionMakerStop, clearQuestionMakerStatus } = qasQuestionMakerSlice.actions;

export const selectGeneratingQuestionsStartStatus = (state: RootState) => state.qasQuestionMaker.questionMakerStart;
export const selectGeneratingQuestionsStopStatus = (state: RootState) => state.qasQuestionMaker.questionMakerStop;
export const selectGeneratingQuestionsProcessStatus = (state: RootState) => state.qasQuestionMaker.questionMakerStatus;

export default qasQuestionMakerSlice.reducer;
